import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { type SupportedNetworkName } from '@pkg/types';

import { callCompetitionCollectionValidity } from '@app/client/resources/competitions';
import { useFantasyCollectionValidityStore, useFantasyMetadataStore } from '@app/store/zustand';

type ValidityProps = {
    collectionSlugs: string[];
    ignoreSignal?: boolean;
    chain?: SupportedNetworkName;
    updateStore?: boolean;
};
export const COLLECTION_VALIDITY_CACHE = 'collection_validity_cache';
export const useGetFantasyCollectionValidity = () => {
    const queryClient = useQueryClient();
    const { setCollectionsValidity } = useFantasyCollectionValidityStore();

    const { currentGame } = useFantasyMetadataStore();
    const currentGameId = currentGame?.id;

    return useCallback(
        async ({ collectionSlugs, ignoreSignal, chain, updateStore = false }: ValidityProps) => {
            if (!collectionSlugs?.length || !currentGameId?.length) return;
            const customQuery = `?slugs=${collectionSlugs.join(',')}&network=${chain}`;
            const cache = [COLLECTION_VALIDITY_CACHE, currentGameId, customQuery];
            const validationResponse = await queryClient.ensureQueryData({
                queryKey: cache,
                queryFn: ({ signal }) =>
                    callCompetitionCollectionValidity({
                        competitionId: currentGameId || '',
                        customQuery,
                        settings: { signal: ignoreSignal ? undefined : signal },
                    }),

                retry: 3,
            });
            if (validationResponse.ok && validationResponse.value) {
                if (updateStore) {
                    setCollectionsValidity(validationResponse.value.dynamic_criteria);
                }
                return validationResponse.value.dynamic_criteria;
            }
        },
        [currentGameId, queryClient, setCollectionsValidity],
    );
};
